/**
 * PointsOfInterestLayer Component - Points of interest markers rendering
 * Displays hospitals, prisons, nuclear facilities, government buildings as informational markers
 * Note: This is informational only - verify local restrictions before flight
 */
import { Marker, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';

// Category colors and icons
const CATEGORY_CONFIG = {
  hospital: {
    color: '#EF4444',      // Red
    bgColor: '#FEE2E2',
    icon: '🏥'
  },
  prison: {
    color: '#6B7280',      // Gray
    bgColor: '#F3F4F6',
    icon: '🔒'
  },
  nuclear: {
    color: '#F59E0B',      // Amber
    bgColor: '#FEF3C7',
    icon: '☢️'
  },
  government: {
    color: '#3B82F6',      // Blue
    bgColor: '#DBEAFE',
    icon: '🏛️'
  },
  military: {
    color: '#059669',      // Green
    bgColor: '#D1FAE5',
    icon: '⚔️'
  }
};

// Create custom icon for each category
const createCategoryIcon = (category) => {
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.government;
  
  return L.divIcon({
    className: 'poi-icon',
    html: `<div style="
      background: ${config.color};
      border-radius: 50%;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      font-size: 14px;
    ">${config.icon}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14]
  });
};

/**
 * PointsOfInterestLayer - Renders points of interest markers (informational only)
 * @param {Array} objects - Array of POI GeoJSON features
 * @param {boolean} showLayer - Whether to show the layer
 * @param {string} language - 'cs' or 'en' for translations
 */
export const PointsOfInterestLayer = ({ objects, showLayer, language = 'cs' }) => {
  if (!showLayer || !objects || objects.length === 0) {
    return null;
  }
  
  const translations = {
    cs: {
      verifyRestrictions: 'Ověřte místní omezení před letem',
      informationalNote: 'Informativní zobrazení - nenahrazuje oficiální zdroje',
      distance: 'Vzdálenost',
      categories: {
        hospital: 'Nemocnice',
        prison: 'Věznice',
        nuclear: 'Jaderné zařízení',
        government: 'Vládní budova',
        military: 'Vojenský objekt'
      }
    },
    en: {
      verifyRestrictions: 'Verify local restrictions before flight',
      informationalNote: 'Informational display - does not replace official sources',
      distance: 'Distance',
      categories: {
        hospital: 'Hospital',
        prison: 'Prison',
        nuclear: 'Nuclear Facility',
        government: 'Government Building',
        military: 'Military Area'
      }
    }
  };
  
  const t = translations[language] || translations.cs;
  
  return (
    <>
      {objects.map((obj, index) => {
        if (!obj.geometry || obj.geometry.type !== 'Point') return null;
        
        const props = obj.properties || {};
        const coords = obj.geometry.coordinates;
        const position = [coords[1], coords[0]]; // GeoJSON is [lon, lat], Leaflet is [lat, lon]
        
        const category = props.category || 'government';
        const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.government;
        
        const name = language === 'en' ? (props.name_en || props.name) : props.name;
        const description = language === 'en' ? (props.description_en || props.description) : props.description;
        const categoryLabel = t.categories[category] || category;
        
        return (
          <Marker
            key={`poi-${obj.id || index}`}
            position={position}
            icon={createCategoryIcon(category)}
          >
            <Popup maxWidth={280} className="poi-popup">
              <div style={{ minWidth: '200px', fontFamily: 'system-ui, sans-serif' }}>
                {/* Header */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginBottom: '8px',
                  paddingBottom: '8px',
                  borderBottom: `2px solid ${config.bgColor}`
                }}>
                  <div style={{ 
                    background: config.bgColor, 
                    borderRadius: '8px', 
                    padding: '6px',
                    fontSize: '18px',
                    lineHeight: 1
                  }}>
                    {config.icon}
                  </div>
                  <div>
                    <strong style={{ color: config.color, fontSize: '13px', display: 'block' }}>
                      {name}
                    </strong>
                    <span style={{ 
                      fontSize: '10px', 
                      color: '#666',
                      background: config.bgColor,
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {categoryLabel}
                    </span>
                  </div>
                </div>
                
                {/* Description */}
                {description && (
                  <p style={{ margin: '0 0 8px 0', fontSize: '11px', color: '#666' }}>
                    {description}
                  </p>
                )}
                
                {/* Informational notice */}
                <div style={{ 
                  background: '#E0F2FE', 
                  borderRadius: '6px', 
                  padding: '8px',
                  borderLeft: '3px solid #0EA5E9'
                }}>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '11px', 
                    fontWeight: 600, 
                    color: '#0369A1' 
                  }}>
                    ℹ️ {t.verifyRestrictions}
                  </p>
                  <p style={{ 
                    margin: '4px 0 0 0', 
                    fontSize: '9px', 
                    color: '#0369A1',
                    opacity: 0.8
                  }}>
                    {t.informationalNote}
                  </p>
                </div>
                
                {/* Distance */}
                {props.distance_km && (
                  <div style={{ marginTop: '6px', fontSize: '10px', color: '#888' }}>
                    {t.distance}: {props.distance_km} km
                  </div>
                )}
              </div>
            </Popup>
            
            <Tooltip direction="top" offset={[0, -14]}>
              <span style={{ fontWeight: 500 }}>{config.icon} {name}</span>
            </Tooltip>
          </Marker>
        );
      })}
    </>
  );
};

// Export with old name for compatibility
export { PointsOfInterestLayer as CriticalObjectsLayer };
export default PointsOfInterestLayer;
